import React, { useEffect, useMemo, useRef, useState } from 'react'
import JoditEditor from 'jodit-react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import secureLocalStorage from 'react-secure-storage'
import { axiosInstance } from '../Config'
import policyImg from "../../Assets/Images/policy.png"
import { usePermissionData } from '../../AppContext'

const PrivacyPolicy = () => {
    const credential = secureLocalStorage.getItem('admin_credentials')
    const { checkAccess } = usePermissionData()
    const editorRef = useRef(null)

    const [content, setContent] = useState('')
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const editorConfig = useMemo(() => ({
        readonly: false,
        height: 520,
        toolbarSticky: true,
        toolbarStickyOffset: 0,
        buttons: [
            'bold', 'italic', 'underline', 'strikethrough', '|',
            'ul', 'ol', '|',
            'font', 'fontsize', 'paragraph', '|',
            'link', 'table', '|',
            'align', 'indent', 'outdent', '|',
            'undo', 'redo', '|',
            'hr', 'eraser', 'fullsize',
        ],
        uploader: { insertImageAsBase64URI: true },
        placeholder: 'Enter your Privacy Policy content here...',
        style: { fontFamily: 'inherit', fontSize: '14px' },
        showWordsCounter: false,
        showCharsCounter: false,
        showXPathInStatusbar: false,
        theme: 'default',
    }), [])

    useEffect(() => {
        document.title = 'Privacy Policy | Mindora'
        window.scrollTo(0, 0)
        fetchContent()
    }, [])

    const fetchContent = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get('privacy-policy', {
                headers: { Authorization: `Bearer ${credential?.token}` }
            })
            setContent(res.data.data.policyContent ?? '')
            setLastUpdatedAt(res.data.data.lastUpdatedAt ?? null)
        } catch (err) {
            swal('', err?.response?.data?.message || 'Failed to load Privacy Policy.', 'error')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        if (!content.trim() || content === '<p><br></p>') {
            swal('', 'Privacy Policy content cannot be empty.', 'warning')
            return
        }
        setSaving(true)
        try {
            const res = await axiosInstance.put('privacy-policy', { policyContent: content }, {
                headers: { Authorization: `Bearer ${credential?.token}` }
            })
            setLastUpdatedAt(res.data.data?.lastUpdatedAt ?? null)
            swal('', res.data?.message || 'Privacy Policy saved successfully!', 'success')
        } catch (err) {
            swal('', err?.response?.data?.message || 'Failed to save Privacy Policy.', 'error')
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title="Privacy Policy" description="Manage the privacy policy shown to users in the app">
                <section className="ps-dashboards p-4">

                    {/* Info Banner */}
                    <div className="rounded-4 p-4 mb-4 position-relative overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, #0CACC2 0%, #A44CD7 100%)' }}>
                        <div style={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                        <div style={{ position: 'absolute', bottom: -40, right: 160, width: 110, height: 110, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <div className="position-relative d-flex align-items-center gap-3">
                            <span style={{ fontSize: 40, lineHeight: 1, flexShrink: 0 }}><img height={40} src={policyImg} /></span>
                            <div className="flex-grow-1">
                                <h5 className="fw-bold mb-1" style={{ color: '#fff' }}>Privacy Policy</h5>
                                <p className="mb-0" style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14 }}>
                                    This content will be displayed to users when they first open the app and in the app settings.
                                </p>
                            </div>
                            {checkAccess(13, 3) && (
                                <button
                                    type="button"
                                    className="button btn-green"
                                    style={{ flexShrink: 0 }}
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving
                                        ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Saving…</>
                                        : <><i className="bi bi-floppy me-2"></i>Save Changes</>
                                    }
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Editor Card */}
                    <div className="rounded-4 p-4"
                        style={{ background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 38, height: 38, background: 'rgba(12,172,194,0.10)' }}>
                                <i className="bi bi-file-earmark-text" style={{ fontSize: 18, color: '#0CACC2' }}></i>
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold" style={{ color: '#1a1a2e', fontSize: 15 }}>Policy Content</h6>
                                <p className="mb-0" style={{ fontSize: 12, color: '#999' }}>
                                    {lastUpdatedAt
                                        ? <>Last updated: <strong>{new Date(lastUpdatedAt).toLocaleString()}</strong></>
                                        : 'Use the editor below to write or update your privacy policy.'
                                    }
                                </p>
                            </div>
                        </div>

                        {!loading && (
                            <JoditEditor
                                ref={editorRef}
                                value={content}
                                config={editorConfig}
                                onBlur={newContent => setContent(newContent)}
                            />
                        )}
                    </div>
                </section>
            </ContainerDashboard>
        </>
    )
}

export default PrivacyPolicy
